// An achievement as a 3D collectible: the card face (AchievementCardFace)
// tilting in perspective (useCardTilt), with the medal and text slightly off
// the face and the light sweeping over everything as it turns. It needs a
// perspective on an ancestor.
//
// - `float` (the celebration): it moves on its own, follows the mouse, and
//   casts a shadow that moves against the tilt.
// - `hover` (the achievements page, where many sit together): it rests flat
//   and tilts, lit, only while the mouse is over it.

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import AchievementCardFace, {
  type AchievementCardFaceProps,
} from './AchievementCardFace';
import CardLight from './CardLight';
import { useCardTilt } from './useCardTilt';

const PRESERVE_3D = { transformStyle: 'preserve-3d' } as const;

// A pointer this close to the top-right corner rests the card flat, so the
// corner control holds still for the click.
const CORNER_REST_PX = 96;

type AchievementCollectibleProps = Omit<
  AchievementCardFaceProps,
  'className' | 'style' | 'children'
> & {
  mode: 'float' | 'hover';
  /** A control in the top-right corner, e.g. the history button. */
  corner?: React.ReactNode;
};

const AchievementCollectible: React.FC<AchievementCollectibleProps> = ({
  mode,
  corner,
  ...face
}) => {
  const floating = mode === 'float';
  const {
    reduceMotion,
    rotateX,
    rotateY,
    glare,
    foilPosition,
    shadow,
    pointerHandlers,
    rest,
  } = useCardTilt({ idle: floating });
  const [hovered, setHovered] = useState(false);

  const followPointer = (event: React.PointerEvent<HTMLDivElement>) => {
    // Measured on the wrapper, which never tilts, so the corner zone does not
    // shift as the card settles.
    const box = event.currentTarget.getBoundingClientRect();
    const nearCorner =
      corner !== undefined &&
      event.clientX > box.right - CORNER_REST_PX &&
      event.clientY < box.top + CORNER_REST_PX;
    setHovered(!nearCorner && event.pointerType === 'mouse');
    if (nearCorner) rest();
    else pointerHandlers.onPointerMove(event);
  };

  return (
    <div
      className="w-full"
      style={PRESERVE_3D}
      onPointerMove={followPointer}
      onPointerLeave={() => {
        setHovered(false);
        pointerHandlers.onPointerLeave();
      }}
    >
      <motion.div
        className="rounded-xl"
        style={
          reduceMotion
            ? undefined
            : {
                ...PRESERVE_3D,
                rotateX,
                rotateY,
                ...(floating && { boxShadow: shadow }),
              }
        }
      >
        <AchievementCardFace
          {...face}
          style={PRESERVE_3D}
          className={floating ? undefined : 'shadow-sm'}
        >
          {corner && (
            <motion.div className="absolute top-2 right-2" style={{ z: 8 }}>
              {corner}
            </motion.div>
          )}
          {!reduceMotion && (
            // In front of the content, so the medal, text, and chip catch the
            // light with the card and read as one laminated surface. Scaled
            // down to cancel the perspective growth of its depth.
            <motion.div
              className="pointer-events-none absolute inset-0"
              style={{ z: 16, scale: 0.982 }}
              initial={false}
              animate={{ opacity: floating || hovered ? 1 : 0 }}
              transition={{ duration: 0.25 }}
            >
              <CardLight glare={glare} foilPosition={foilPosition} />
            </motion.div>
          )}
        </AchievementCardFace>
      </motion.div>
    </div>
  );
};

export default AchievementCollectible;
